import React, { useState, useEffect } from 'react';
import { IconChevronRight } from '@americanexpress/dls-icons';
import { useAnalyticsContext } from '@/context/analyticsContext';
import {
  getStickyGlobalHeaderOffset,
  smoothScrollToElement,
} from '@/helpers/utils';

const CENTURION_ANCHOR = 'centurion-lounges-section';
const SIDECAR_ANCHOR = 'sidecar-lounges-section';

interface CenturionNetworkNavProps {
  navHeader: string;
  centurionHeader: string;
  centurionDescription: string;
  sidecarHeader: string;
  sidecarDescription: string;
}

const scrollToAnchor = (anchorId: string) => {
  const el = document.querySelector(`#${anchorId}`);
  if (el) {
    smoothScrollToElement({
      element: el,
      offset: getStickyGlobalHeaderOffset(),
    });
  }
};

const CenturionNetworkNav: React.FC<CenturionNetworkNavProps> = ({
  navHeader,
  centurionHeader,
  centurionDescription,
  sidecarHeader,
  sidecarDescription,
}) => {
  const { customTrack, identifier } = useAnalyticsContext();
  const [activeSection, setActiveSection] = useState<string>(CENTURION_ANCHOR);

  const isClickScrolling = React.useRef(false);

  useEffect(() => {
    const sections = [CENTURION_ANCHOR, SIDECAR_ANCHOR];
    const observer = new IntersectionObserver(
      (entries) => {
        if (isClickScrolling.current) return;
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          } else if (
            entry.target.id === SIDECAR_ANCHOR &&
            entry.rootBounds &&
            entry.boundingClientRect.top >= entry.rootBounds.bottom
          ) {
            setActiveSection(CENTURION_ANCHOR);
          }
        });
      },
      { rootMargin: '-20% 0px -60% 0px' }
    );

    sections.forEach((id) => {
      const el = document.querySelector(`#${id}`);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  const handleClick =
    (anchorId: string, label: string) =>
    (e: React.MouseEvent<HTMLButtonElement>) => {
      customTrack({
        event: e,
        identifier,
        element: 'Navigation',
        detail: label,
      });
      setActiveSection(anchorId);
      isClickScrolling.current = true;
      scrollToAnchor(anchorId);
      setTimeout(() => {
        isClickScrolling.current = false;
      }, 1000);
    };

  return (
    <>
      {/* MOBILE: < md */}
      <div className="tw-mb-6 tw-mt-6 tw-block md_tw-hidden">
        <div className="tw-w-full tw-rounded-2xl tw-bg-gray-100 tw-px-6 tw-py-6">
          <div className="tw-mb-4 tw-font-sans tw-text-base tw-font-bold tw-text-gray-700">
            {navHeader}
          </div>
          <button
            type="button"
            onClick={handleClick(CENTURION_ANCHOR, centurionHeader)}
            className="tw-block tw-w-full tw-cursor-pointer tw-border-0 tw-bg-none tw-px-0 tw-py-3 tw-text-left tw-font-sans tw-text-base tw-font-medium tw-leading-6 tw-text-blue-bright-shade-600-s8"
          >
            {centurionHeader}
          </button>
          <button
            type="button"
            onClick={handleClick(SIDECAR_ANCHOR, sidecarHeader)}
            className="tw-block tw-w-full tw-cursor-pointer tw-border-0 tw-bg-none tw-px-0 tw-py-3 tw-text-left tw-font-sans tw-text-base tw-font-medium tw-leading-6 tw-text-blue-bright-shade-600-s8"
          >
            {sidecarHeader}
          </button>
        </div>
      </div>

      {/* DESKTOP: >= md */}
      <div className="tw-hidden md_tw-block">
        <div>
          <h2>
            <button
              type="button"
              onClick={handleClick(CENTURION_ANCHOR, centurionHeader)}
              style={{ outline: 'none' }}
              className={`tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-border-0 tw-bg-none tw-px-0 tw-text-left tw-font-sans tw-text-base tw-font-bold tw-leading-6 ${
                activeSection === CENTURION_ANCHOR
                  ? 'tw-text-gray-900'
                  : 'tw-text-gray-700'
              }`}
            >
              {centurionHeader}
              {activeSection !== CENTURION_ANCHOR && (
                <IconChevronRight
                  size="xs"
                  isFilled={true}
                  className="tw-ml-2 svg-children_tw-fill-gray-700"
                />
              )}
            </button>
          </h2>
          {activeSection === CENTURION_ANCHOR && (
            <p className="tw-pt-2 tw-font-dls tw-text-base tw-text-gray-800">
              {centurionDescription}
            </p>
          )}
          <h2>
            <button
              type="button"
              onClick={handleClick(SIDECAR_ANCHOR, sidecarHeader)}
              style={{ outline: 'none' }}
              className={`tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-border-0 tw-bg-none tw-px-0 tw-pt-6 tw-text-left tw-font-sans tw-text-base tw-font-bold tw-leading-6 ${
                activeSection === SIDECAR_ANCHOR
                  ? 'tw-text-gray-900'
                  : 'tw-text-gray-700'
              }`}
            >
              {sidecarHeader}
              {activeSection !== SIDECAR_ANCHOR && (
                <IconChevronRight
                  size="xs"
                  isFilled={true}
                  className="tw-ml-2 svg-children_tw-fill-gray-700"
                />
              )}
            </button>
          </h2>
          {activeSection === SIDECAR_ANCHOR && (
            <p className="tw-pt-2 tw-font-dls tw-text-base tw-text-gray-800">
              {sidecarDescription}
            </p>
          )}
        </div>
      </div>
    </>
  );
};

export default CenturionNetworkNav;
